import React, { useEffect, useState } from 'react';
import { CommunicationLog } from '@/types';
import { Mail, Smartphone, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { Pagination } from '@shared/components/common/Pagination';
import { formatUkDate, formatUkTime } from '@/lib/dates';
import { htmlToPlainText } from '@/lib/formatters';
import { api } from '@/services/api';

interface CustomerDispatchLogsProps {
  customerId: string;
}

function getStatusIcon(status: string): React.ReactNode {
  switch (status) {
    case 'sent':
      return <CheckCircle2 className="w-3 h-3 text-green-500" />;
    case 'queued':
      return <Clock className="w-3 h-3 text-amber-500" />;
    case 'failed':
      return <AlertCircle className="w-3 h-3 text-red-500" />;
    default:
      return null;
  }
}

export const CustomerDispatchLogs: React.FC<CustomerDispatchLogsProps> = ({ customerId }) => {
  const [items, setItems] = useState<CommunicationLog[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [customerId]);

  useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    api
      .listStaffCustomerLogs(customerId, { page, pageSize })
      .then((data) => {
        if (!isActive) return;
        setItems(data.items || []);
        setTotalCount(Number(data.totalCount || 0));
      })
      .catch(() => {
        if (!isActive) return;
        setItems([]);
        setTotalCount(0);
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, [customerId, page, pageSize]);

  return (
    <div>
      <h4 className="font-black text-xs text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
        <Smartphone className="w-4 h-4" /> Dispatch Logs
      </h4>
      <div className="space-y-4">
        {isLoading ? (
          <p className="text-slate-500 italic text-sm">Loading logs...</p>
        ) : items.length === 0 ? (
          <p className="text-slate-500 italic text-sm">No communications recorded.</p>
        ) : (
          items.map((log) => {
            const timestamp = log.sentAt || log.createdAt;
            return (
              <div
                key={log.id}
                className="bg-white border rounded-xl p-4 shadow-sm group hover:border-blue-200 transition-all"
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-2">
                    <div
                      className={`p-1.5 rounded-lg ${
                        log.type === 'email'
                          ? 'bg-blue-50 text-blue-600'
                          : 'bg-purple-50 text-purple-600'
                      }`}
                    >
                      {log.type === 'email' ? (
                        <Mail className="w-3.5 h-3.5" />
                      ) : (
                        <Smartphone className="w-3.5 h-3.5" />
                      )}
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">
                        {log.type}
                      </p>
                      <p className="text-xs font-bold text-slate-700 mt-1">
                        {log.subject || (log.type === 'sms' ? 'SMS Message' : 'No Subject')}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-slate-400 text-[10px] font-bold block">
                      {timestamp ? formatUkTime(timestamp, '—') : '—'}
                    </span>
                    <span className="text-slate-400 text-[10px] block">
                      {formatUkDate(timestamp, '—', { preserveTimeZone: true })}
                    </span>
                  </div>
                </div>
                <div className="bg-slate-50 p-2.5 rounded-lg border border-slate-100 text-xs text-slate-600 leading-relaxed italic line-clamp-2 group-hover:line-clamp-none transition-all">
                  "{log.type === 'email' ? htmlToPlainText(log.content) : log.content}"
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200">
                    {getStatusIcon(log.status)}
                    <span className="text-[9px] font-black uppercase text-slate-500">
                      {log.status}
                    </span>
                  </div>
                  <span className="text-[9px] font-mono text-slate-300">Log ID: {log.id}</span>
                </div>
              </div>
            );
          })
        )}
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        isLoading={isLoading}
      />
    </div>
  );
};
